import React, { useCallback, useRef, useState } from 'react';
import TrackArt from '../TrackArt';
import AutomixTrackMenu, { coverOf } from './AutomixTrackMenu';

// A wand, not sparkles: sparkles read as "AI", which this is not.
export const WandIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M4 20l10-10" />
        <path d="M15 3v2.5" /><path d="M15 12.5V15" /><path d="M9.5 8.5H12" /><path d="M18 8.5h2.5" />
        <path d="M17.5 6l1.8-1.8" /><path d="M11.8 6l-1.3-1.3" /><path d="M17.5 11l1.3 1.3" />
    </svg>
);

const Plus = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
        <path d="M12 6v12" /><path d="M6 12h12" />
    </svg>
);

/** One end of the set: the pinned record, or "MixGoat picks" with a + to choose one. */
function End({ kicker, label, track, options, value, otherKey, otherLabel, onChoose }) {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    return (
        <div className={`amx-end${track ? ' is-set' : ''}`}>
            <button
                type="button"
                ref={anchorRef}
                className="amx-end-main"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                {track
                    ? <TrackArt className="amx-end-cover" src={coverOf(track)} size={32} />
                    : <span className="amx-end-cover is-empty"><Plus /></span>}
                <span className="amx-end-text">
                    <span className="amx-end-k">{kicker}</span>
                    <span className={`amx-end-v${track ? '' : ' is-auto'}`} title={track?.title}>{track ? track.title : 'MixGoat picks'}</span>
                </span>
            </button>
            {track && (
                <button type="button" className="amx-end-clear" aria-label={`Let MixGoat pick the ${label.toLowerCase()}`} title="Let MixGoat pick" onClick={() => onChoose(null)}>
                    ×
                </button>
            )}
            {open && (
                <AutomixTrackMenu
                    anchorEl={anchorRef.current}
                    label={label}
                    options={options}
                    value={value}
                    otherKey={otherKey}
                    otherLabel={otherLabel}
                    onChoose={onChoose}
                    onClose={close}
                />
            )}
        </div>
    );
}

/**
 * "Start and finish": the two ends of a Best-order set, with MixGoat's part
 * in between spelled out — pin an opener or closer, or leave it to MixGoat —
 * and the way to see that part: Reveal, with its progress in the same line.
 *
 * @param {object} reveal {phase: 'idle'|'running'|'stopping', percent, detail,
 *   shown (the order is on screen), error, disabled (the tracks aren't ready), onReveal, onStop}
 */
const AutomixBookends = ({ tracks, openerKey, closerKey, onOpener, onCloser, reveal }) => {
    const opener = tracks.find((t) => t.key === openerKey) || null;
    const closer = tracks.find((t) => t.key === closerKey) || null;
    const between = Math.max(0, tracks.length - (opener ? 1 : 0) - (closer ? 1 : 0));
    const busy = reveal.phase !== 'idle';
    let middle;
    if (busy) {
        middle = (
            <>
                <span className="amx-between-progress" aria-hidden="true"><span style={{ width: `${Math.max(3, Math.round(reveal.percent || 0))}%` }} /></span>
                <span className="amx-between-text" aria-live="polite">{reveal.phase === 'stopping' ? 'Stopping…' : reveal.detail}</span>
                <button type="button" className="amx-link is-quiet" disabled={reveal.phase === 'stopping'} onClick={reveal.onStop}>Stop</button>
            </>
        );
    } else if (reveal.error) {
        middle = (
            <>
                <span className="amx-between-text is-warn" title={reveal.error.message}>Couldn’t reveal the order</span>
                <button type="button" className="amx-link" onClick={reveal.onReveal}>Try again</button>
            </>
        );
    } else {
        middle = (
            <>
                <span className="amx-between-text">{between} {between === 1 ? 'track' : 'tracks'} in MixGoat’s order</span>
                {!reveal.shown && (
                    <button type="button" className="amx-reveal" disabled={!!reveal.disabled} onClick={reveal.onReveal}>
                        <WandIcon />
                        Reveal
                    </button>
                )}
            </>
        );
    }
    return (
        <div className="amx-ends">
            <End kicker="Opens with" label="Opener" track={opener} options={tracks} value={openerKey} otherKey={closerKey} otherLabel="Closer" onChoose={onOpener} />
            <div className={`amx-between${busy ? ' is-busy' : ''}`}><i aria-hidden="true" /><div className="amx-between-body">{middle}</div></div>
            <End kicker="Closes with" label="Closer" track={closer} options={tracks} value={closerKey} otherKey={openerKey} otherLabel="Opener" onChoose={onCloser} />
        </div>
    );
};

export default AutomixBookends;
